import { AnanseApiService } from "app/services/Ananse/AnanseApiService";
import { useEffect, useState } from "react";
import {
  TableRow,
  TableCell,
  Tooltip,
  Button,
  ButtonGroup,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { QuizAnswerCognitiveType } from "interfaces";
import { formatedDate } from "utils";
import Loading from "app/pages/home/components/Loading";
import { useTranslation } from "_metronic/i18n/language";
import { AudiometryAnswerType, PatientAccountType } from "app/types";
import { showMensage } from "app/services/FeaturesElement";
import DataTable from "app/pages/admin/components/DataTable";
import { useAudiometryResultModel } from "../useAudiometryResultModel";
import ModalAudiometryDetails from "../components/ModalAudiometryDetails";

export type ParamsI = {
  id: number | undefined;
  name: string;
};

export type ResultQuizCognitiveType = {
  testResultId: string;
  results: QuizAnswerCognitiveType[];
};

type AudiometryResultViewProps = ReturnType<typeof useAudiometryResultModel> & {
  getQuizNameById: (id: number) => Promise<string>;
};

const AudiometryResultView = (props: AudiometryResultViewProps) => {
  const { getAudiometryResults, getQuizNameById, findQuestion } = props;
  const translate = useTranslation();
  const api = new AnanseApiService();
  const params: any = useParams();
  const account: ParamsI | undefined = params;
  const [loading, setLoading] = useState<boolean>(false);
  const [results, setResults] = useState<AudiometryAnswerType[]>([]);
  const [distinctQuizzes, setDistinctQuizzes] = useState<
    AudiometryAnswerType[]
  >([]);
  const [resultSelected, setResultSelected] = useState<
    AudiometryAnswerType[]
  >();
  const [openModal, setOpenModal] = useState(false);
  const [nameTitle, setNameTitle] = useState<string>("");
  const [quizName, setQuizName] = useState<string>("");
  const [accountSelected, setAccountSelected] = useState(
    {} as PatientAccountType,
  );
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    loadResults();
  }, []);

  const loadResults = async () => {
    setLoading(true);
    try {
      loadAccount(Number(id));
      const result: AudiometryAnswerType[] = await getAudiometryResults(
        Number(id),
      );

      const uniqueResultsMap = new Map<string, AudiometryAnswerType>();

      result.forEach(item => {
        if (!uniqueResultsMap.has(item.testResultId)) {
          uniqueResultsMap.set(item.testResultId, item);
        }
      });

      const distinctResults = Array.from(uniqueResultsMap.values());

      setResults(result);
      setDistinctQuizzes(distinctResults);
    } catch (error) {
      console.error("ERROR: ", error);
      showMensage(translate, false, translate("defaultMessages.loadData"));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (isOpen: boolean) => {
    setOpenModal(isOpen);
  };

  const loadAccount = async (
    accountId: number | undefined,
  ): Promise<PatientAccountType | any> => {
    try {
      if (!accountId) {
        throw new Error("Account ID is undefined");
      }
      const response: PatientAccountType = await api.makeHttpRequest({
        url: `/account/${accountId}`,
      });
      setAccountSelected(response);
      setNameTitle(
        translate("screenApp.anamnese.list.resultsOf") + response?.name,
      );
    } catch (error) {
      console.error("error getAccount: ", error);
    }
  };

  const headColumns = [
    { id: "none", label: translate("screens.dashboard.action") },
    { id: "title", label: translate("screens_quiz_list_title") },
    { id: "createdAt", label: translate("screenApp.results.examDate") },
    {
      id: "testResultId",
      label: translate("screenApp.results.numberTest"),
    },
    { id: "doctor", label: translate("screenApp.results.title") },
    { id: "email", label: translate("screens.login.email") },
  ];

  const gerarResultado = (value: AudiometryAnswerType) => {
    const allTestResults = results.filter(
      result => result.testResultId === value.testResultId,
    );
    setResultSelected(allTestResults);
    setOpenModal(true);
  };

  const formatRows = (result: AudiometryAnswerType, labelId: any) => {
    return (
      <TableValues
        result={result}
        labelId={labelId}
        getQuizNameById={getQuizNameById}
        date={formatedDate(result.createdAt, true, true)}
        gerarResultado={gerarResultado}
        translate={translate}
        onQuizNameChange={setQuizName}
      />
    );
  };

  return (
    <div
      style={{
        paddingBottom: "50px",
      }}
    >
      <Loading isLoading={loading} />

      {resultSelected && (
        <ModalAudiometryDetails
          quizAnswer={resultSelected}
          openModal={openModal}
          findQuestion={findQuestion}
          setOpenModal={handleOpenModal}
          loading={loading}
          setLoading={setLoading}
          accountId={account}
          accountSelected={accountSelected}
          quizName={quizName}
        />
      )}

      <DataTable
        formatRows={formatRows}
        headColumns={headColumns}
        loading={loading}
        translate={translate}
        rows={distinctQuizzes}
        placeholderSearch={
          translate("screenApp.anamnese.list.search") +
          " " +
          translate("screenApp.results.numberTest")
        }
        onRefresh={loadResults}
        router="/admin/account"
        titleTable={nameTitle}
      />
    </div>
  );
};

type TableValuesProps = {
  result: AudiometryAnswerType;
  labelId: string;
  getQuizNameById: (id: number) => Promise<string>;
  translate: (key: string) => string;
  date: string;
  gerarResultado: (value: AudiometryAnswerType) => void;
  onQuizNameChange: (name: string) => void;
};

const TableValues = ({
  result,
  labelId,
  getQuizNameById,
  translate,
  date,
  gerarResultado,
  onQuizNameChange,
}: TableValuesProps) => {
  const [quizName, setQuizName] = useState("");

  useEffect(() => {
    const load = async () => {
      const quiz = await getQuizNameById(result.quizId);
      setQuizName(quiz);
      onQuizNameChange(quiz);
    };
    load();
  }, [result.quizId]);

  return (
    <TableRow hover tabIndex={-1} key={result.id} sx={{ cursor: "pointer" }}>
      <TableCell>
        <ButtonGroup size="small" aria-label="small button group">
          <Tooltip title={translate("buttons_detail")}>
            <Button
              size="small"
              variant="contained"
              onClick={() => gerarResultado(result)}
            >
              {translate("screenApp_listPacient_buttonsPacient_buttonDetail")}
            </Button>
          </Tooltip>
        </ButtonGroup>
      </TableCell>
      <TableCell align="inherit">{quizName}</TableCell>
      <TableCell align="inherit">{result.createdAt ? date : null}</TableCell>
      <TableCell id={labelId} align="inherit">
        {result.testResultId}
      </TableCell>
      <TableCell id={labelId} align="inherit">
        {result.doctor}
      </TableCell>
      <TableCell id={labelId} align="inherit">
        {result.email}
      </TableCell>
    </TableRow>
  );
};

export default AudiometryResultView;
